import { Link } from "react-router-dom";
import { ShieldCheck, Mail, Phone } from "lucide-react";

const UPDATED = "June 1, 2026";
const COMPANY = "Kavuma Technologies LLC, DBA RideStamp";

const SECTIONS = [
  {
    id: "intro",
    title: "1. Introduction",
    body: [
      `${COMPANY} ("RideStamp," "we," "us," or "our") provides an operations and accountability platform for non-emergency and non-medical transportation providers. This Privacy Policy explains how we collect, use, disclose, and safeguard information when you use our websites, web portal, and related services (collectively, the "Services").`,
      "By using the Services, you agree to the collection and use of information in accordance with this Policy. If you do not agree, please do not use the Services.",
    ],
  },
  {
    id: "scope",
    title: "2. Who This Policy Covers",
    body: [
      "This Policy applies to transportation providers and their authorized personnel (Owners, Supervisors, Dispatchers, and Administrators) who access the RideStamp portal, as well as visitors to our public website.",
      "Where RideStamp processes data about riders or passengers on behalf of a transportation provider, the provider is the data controller and RideStamp acts as a service provider/processor. In those cases, the provider's own privacy notice governs how rider information is used.",
    ],
  },
  {
    id: "collect",
    title: "3. Information We Collect",
    list: [
      "Account & organization information: name, business name, email address, phone number, role, and credentials used to sign in.",
      "Operational data you enter: drivers, vehicles, trips, schedules, dispatch records, incidents, compliance records, maintenance and repair costs, and trip evidence.",
      "Location & GPS data: real-time and historical vehicle/driver location used for live visibility, late-trip prevention, and accountability.",
      "Usage & device data: log data, IP address, browser type, pages viewed, and actions taken within the Services.",
      "Communications: messages you send us, including demo requests and contact-form submissions.",
    ],
  },
  {
    id: "use",
    title: "4. How We Use Information",
    list: [
      "Provide, operate, secure, and improve the Services.",
      "Authenticate users and enforce role-based access and organization (tenant) isolation.",
      "Power live GPS visibility, late-trip alerts, dispatch, compliance tracking, incident management, and reporting.",
      "Respond to inquiries, demo requests, and support needs.",
      "Monitor for fraud, abuse, and security incidents, and comply with legal obligations.",
    ],
  },
  {
    id: "location",
    title: "5. Location & GPS Data",
    body: [
      "Because RideStamp supports live operations, we process vehicle and driver location data to display real-time positions, detect late-trip risk, and maintain an accountability trail. Location data is associated with the relevant organization and is accessible only to that organization's authorized users according to their role.",
      "Transportation providers are responsible for notifying their drivers and personnel about location tracking and obtaining any consents required by applicable law.",
    ],
  },
  {
    id: "share",
    title: "6. How We Share Information",
    body: ["We do not sell your personal information. We share information only as described below:"],
    list: [
      "Within your organization: with other authorized users according to their assigned role and permissions.",
      "Service providers: vendors who host, store, or support the Services under contractual confidentiality and security obligations.",
      "Legal & safety: when required by law, regulation, legal process, or to protect the rights, property, or safety of RideStamp, our customers, or the public.",
      "Business transfers: in connection with a merger, acquisition, financing, or sale of assets, subject to this Policy.",
    ],
  },
  {
    id: "retention",
    title: "7. Data Retention",
    body: [
      "We retain information for as long as your organization maintains an active account and as needed to provide the Services, comply with legal and contractual obligations, resolve disputes, and enforce our agreements. Upon account termination, we will delete or de-identify data within a commercially reasonable period, unless retention is required by law.",
    ],
  },
  {
    id: "security",
    title: "8. Data Security",
    body: [
      "We implement administrative, technical, and organizational measures designed to protect information, including encrypted transport, access controls, role-based permissions, and tenant isolation. No method of transmission or storage is 100% secure, and we cannot guarantee absolute security.",
      "RideStamp will never ask you for your password or payment details by email.",
    ],
  },
  {
    id: "rights",
    title: "9. Your Rights & Choices",
    body: [
      "Depending on your location, you may have rights to access, correct, update, port, or delete personal information, and to object to or restrict certain processing. To exercise these rights, contact us using the details below. Where RideStamp acts as a processor for a transportation provider, we will refer your request to that provider.",
      "Residents of certain U.S. states (such as California) and individuals in the EEA/UK may have additional rights under applicable laws (e.g., CCPA/CPRA, GDPR). We do not sell or share personal information for cross-context behavioral advertising.",
    ],
  },
  {
    id: "cookies",
    title: "10. Cookies & Tracking",
    body: [
      "We use strictly necessary cookies and similar technologies to keep you signed in and to operate the Services. We may use limited analytics to understand usage and improve performance. You can control cookies through your browser settings, though disabling them may affect functionality.",
    ],
  },
  {
    id: "children",
    title: "11. Children's Privacy",
    body: [
      "The Services are intended for business use and are not directed to children under 13 (or the minimum age required by local law). We do not knowingly collect personal information from children.",
    ],
  },
  {
    id: "changes",
    title: "12. Changes to This Policy",
    body: [
      "We may update this Policy from time to time. We will revise the \"Last updated\" date above and, where appropriate, provide additional notice. Your continued use of the Services after changes become effective constitutes acceptance of the revised Policy.",
    ],
  },
];

export default function PrivacyPolicyPage() {
  return (
    <div data-testid="privacy-page">
      <div className="bg-slate-950 pt-36 pb-16 text-white">
        <div className="mx-auto max-w-4xl px-5 lg:px-8">
          <div className="inline-flex items-center gap-2 rounded-full border border-blue-500/30 bg-blue-500/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-blue-300">
            <ShieldCheck className="h-4 w-4" /> Privacy
          </div>
          <h1 className="mt-5 font-display text-4xl font-extrabold tracking-tight sm:text-5xl">Privacy Policy</h1>
          <p className="mt-4 text-slate-400">Last updated: {UPDATED}</p>
        </div>
      </div>

      <div className="mx-auto max-w-4xl px-5 py-16 lg:px-8 lg:py-20">
        {/* Disclaimer */}
        <div className="mb-10 rounded-xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-900" data-testid="privacy-disclaimer">
          <strong>Template notice:</strong> This Privacy Policy is a comprehensive starting draft for {COMPANY}. Please have it reviewed by qualified legal counsel to ensure it fits your specific operations and applicable laws before relying on it.
        </div>

        <div className="space-y-10">
          {SECTIONS.map((s) => (
            <section key={s.id} id={s.id} className="scroll-mt-28" data-testid={`privacy-section-${s.id}`}>
              <h2 className="font-display text-xl font-bold text-slate-900 lg:text-2xl">{s.title}</h2>
              {s.body?.map((p, i) => (
                <p key={i} className="mt-3 text-base leading-relaxed text-slate-600">{p}</p>
              ))}
              {s.list && (
                <ul className="mt-4 space-y-2.5">
                  {s.list.map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-slate-600">
                      <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-blue-600" />
                      <span className="leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}

          {/* Contact */}
          <section id="contact" className="scroll-mt-28" data-testid="privacy-section-contact">
            <h2 className="font-display text-xl font-bold text-slate-900 lg:text-2xl">13. Contact Us</h2>
            <p className="mt-3 text-base leading-relaxed text-slate-600">
              If you have questions about this Policy or our privacy practices, contact us:
            </p>
            <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 p-6">
              <p className="font-display text-base font-bold text-slate-900">{COMPANY}</p>
              <div className="mt-3 space-y-2 text-sm text-slate-600">
                <a href="mailto:support@myridestamp.com" data-testid="privacy-contact-email" className="flex items-center gap-2.5 hover:text-blue-700"><Mail className="h-4 w-4 text-blue-700" /> support@myridestamp.com</a>
                <div className="flex items-center gap-2.5"><Phone className="h-4 w-4 text-blue-700" /> (339) 234-4636</div>
              </div>
            </div>
          </section>
        </div>

        <div className="mt-12 border-t border-slate-200 pt-8 text-sm text-slate-500">
          See also our <Link to="/contact" className="font-semibold text-blue-700 hover:underline" data-testid="privacy-contact-link">Contact</Link> page to reach our team.
        </div>
      </div>
    </div>
  );
}
